/**
 * Translation, as a typed catalogue rather than a runtime lookup.
 *
 * ## Why not a library
 *
 * The usual choice is react-i18next, and its central weakness for a codebase
 * this size is that a missing translation is a **runtime** event: the key falls
 * back, and an English screen quietly shows Danish, or shows `nav.bookkeeping`.
 * Nothing fails; someone notices eventually.
 *
 * Here the Danish catalogue defines the key union and every other catalogue is
 * declared `Record<MessageKey, Message>`, so a translation that has not been
 * written **does not compile**. A gap cannot reach a screen. With two locales
 * and no lazy loading to arrange, that is the whole of what the library would
 * have bought, minus the dependency tree.
 *
 * ## What this does not translate
 *
 * The chart of accounts, the VAT codes, and everything the API returns as
 * domain data stay Danish in every locale — they are Erhvervsstyrelsens
 * standardkontoplan and SKAT's own wording, and an auditor asking about
 * `1010 Salg af varer og ydelser` means that exact string. So this governs the
 * application's chrome over books that remain Danish. See migration 0019.
 */

export const LOCALES = ['da', 'en'] as const;

export type Locale = (typeof LOCALES)[number];

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * One message.
 *
 * A plain string, or a pair for the two grammatical numbers Danish and English
 * share. Anything needing a third form needs a different shape, and adding one
 * is a change to this type rather than a convention someone has to know.
 */
export type Message = string | { readonly one: string; readonly other: string };

export type Catalogue<K extends string> = Readonly<Record<K, Message>>;

/** Values interpolated into `{placeholders}`. */
export type Vars = Readonly<Record<string, string | number>>;

const PLACEHOLDER = /\{(\w+)\}/gu;

/**
 * Fill `{name}` placeholders.
 *
 * A placeholder with no matching variable is left exactly as written rather
 * than replaced with "undefined". On a screen, `{count}` is recognisably a bug;
 * `undefined` reads like data.
 */
export function interpolate(template: string, vars: Vars | undefined): string {
  if (vars === undefined) {
    return template;
  }
  return template.replace(PLACEHOLDER, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Resolve one message against a locale's catalogue.
 *
 * Exported separately from the React hook so it can be used where there is no
 * component — and so it can be tested without rendering anything.
 */
export function translate<K extends string>(
  catalogue: Catalogue<K>,
  key: K,
  vars?: Vars,
): string {
  const message = catalogue[key];
  if (typeof message === 'string') {
    return interpolate(message, vars);
  }
  /*
    Plural selection. `count` is required for a plural message; without it
    there is no honest choice to make, so this takes the "other" form, which is
    the one that reads correctly for an unknown quantity in both languages.
  */
  const count = vars?.['count'];
  const form = count === 1 ? message.one : message.other;
  return interpolate(form, vars);
}
